import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { AuthService } from 'app/um/services/auth-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveServiceFlag } from 'app/um/components/service-flags/save-service-flag';
import * as UM from 'app/um/models/essette-api';
import { ServiceFlagReloadEvent } from 'app/um/lib/events';
import { EventAggregator } from 'aurelia-event-aggregator';

@autoinject()
export class ServiceFlags extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected serviceFlags: UM.AuthFlag[] = [];
    protected editedServiceFlag: UM.AuthFlag | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;
    protected includeInactive: boolean = false;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;

    protected $saveServiceFlagModal!: IPopup;
    protected $form!: IForm;
    protected $serviceFlagForm!: Element;
    protected $saveServiceFlag!: SaveServiceFlag;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private service: AuthService,
        private readonly toaster: Toaster,
        private readonly events: EventAggregator,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected async formSuccess(): Promise<void> {
        this.saveServiceFlagClose();
        await this.reload();
    }

    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        await this.loadServiceFlags();
    }

    protected saveServiceFlag(serviceFlag: UM.AuthFlag): void {
        this.editedServiceFlag = serviceFlag;
        this.$saveServiceFlagModal.open();
    }

    protected saveServiceFlagClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveServiceFlagModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async loadServiceFlags(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();
            const response = await this.service.loadAuthFlags(options, includes, 'services');
            this.serviceFlags = response.results;
            this.resultCount = response.count ?? 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateServiceFlag(serviceFlag: UM.AuthFlag): Promise<void> {
        // Show confirmation dialog only if deactivating
        if(serviceFlag.isActive) {
            const result = await confirmation.showDeactivate('um:auth.flag.serviceFlag.$', 1);
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }

            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateAuthFlag(serviceFlag.id!, !serviceFlag.isActive);
            isSuccessful = response.isSuccessful;
            this.events.publish(new ServiceFlagReloadEvent());
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('um:auth.flag.serviceFlag.$', !serviceFlag.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
